<template>
    <div class="vc-chat">
        <div ref="messagesContainer" class="vc-chat-messages">
            <div v-if="loading" class="vc-chat-state">
                <font-awesome-icon :icon="['fas', 'spinner']" spin class="vc-chat-state__icon" />
                <span>Loading conversation...</span>
            </div>
            <div v-else-if="messages.length === 0" class="vc-chat-state">
                <font-awesome-icon :icon="['fas', 'comments']" class="vc-chat-state__icon" />
                <span>No messages yet. Start the conversation below.</span>
            </div>
            <template v-else>
                <div
                    v-for="msg in messages"
                    :key="msg.id"
                    class="vc-chat-row"
                    :class="isOwnMessage(msg) ? 'vc-chat-row--own' : 'vc-chat-row--other'"
                >
                    <div class="vc-chat-avatar" :class="isOwnMessage(msg) ? 'vc-chat-avatar--own' : 'vc-chat-avatar--other'">
                        {{ avatarInitial(msg) }}
                    </div>
                    <div class="vc-chat-bubble">
                        <div class="vc-chat-label">{{ messageLabel(msg) }}</div>
                        <div class="vc-chat-text">{{ msg.message }}</div>
                        <div class="vc-chat-time">{{ formatDateTime(msg.created_at) }}</div>
                    </div>
                </div>
            </template>
        </div>
        <form v-if="canReply" class="vc-chat-composer" @submit.prevent="sendMessage">
            <textarea
                v-model="replyText"
                rows="2"
                placeholder="Write a message..."
                class="vc-chat-input"
                :disabled="sending"
            ></textarea>
            <button type="submit" class="vc-chat-send" :disabled="sending || !replyText.trim()">
                <font-awesome-icon :icon="['fas', sending ? 'spinner' : 'paper-plane']" :spin="sending" />
                <span>{{ sending ? 'Sending' : 'Send' }}</span>
            </button>
        </form>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const APP_MESSAGES = gql`
  query AppMessages($id: ID!) {
    getVolunteeringApplicationMessages(application_id: $id) {
      error data { id message sender_type created_at user { id name } }
    }
  }
`;

const HOURS_MESSAGES = gql`
  query HoursMessages($id: ID!) {
    getVolunteeringHourSubmissionMessages(hour_submission_id: $id) {
      error data { id message sender_type created_at user { id name } }
    }
  }
`;

const ADD_APP_MSG = gql`
  mutation AddAppMsg($id: ID!, $message: String!) {
    addVolunteeringApplicationMessage(application_id: $id, message: $message) { error message }
  }
`;

const ADD_HOURS_MSG = gql`
  mutation AddHoursMsg($id: ID!, $message: String!) {
    addVolunteeringHourSubmissionMessage(hour_submission_id: $id, message: $message) { error message }
  }
`;

export default {
    name: 'VolunteeringConversation',
    props: {
        type: { type: String, required: true },
        recordId: { type: [String, Number], required: true },
        canReply: { type: Boolean, default: false },
    },
    data() {
        return { messages: [], loading: false, sending: false, replyText: '' };
    },
    watch: {
        recordId: { immediate: true, handler() { this.fetchMessages(); } },
    },
    methods: {
        async fetchMessages() {
            if (!this.recordId) return;
            this.loading = true;
            const query = this.type === 'hours' ? HOURS_MESSAGES : APP_MESSAGES;
            try {
                const { data } = await this.$apollo.query({
                    query: query,
                    variables: { id: String(this.recordId) },
                    fetchPolicy: 'network-only',
                });
                const key = this.type === 'hours' ? 'getVolunteeringHourSubmissionMessages' : 'getVolunteeringApplicationMessages';
                this.messages = data[key].data || [];
                this.$nextTick(this.scrollToBottom);
            } catch (e) { console.log(e); }
            this.loading = false;
        },
        async sendMessage() {
            if (!this.replyText.trim()) return;
            this.sending = true;
            const mutation = this.type === 'hours' ? ADD_HOURS_MSG : ADD_APP_MSG;
            try {
                await this.$apollo.mutate({
                    mutation: mutation,
                    variables: { id: String(this.recordId), message: this.replyText.trim() },
                });
                this.replyText = '';
                this.fetchMessages();
            } catch (e) { console.log(e); }
            this.sending = false;
        },
        isOwnMessage(msg) {
            const userId = this.$store.getters.user_id;
            return msg.user && String(msg.user.id) === String(userId);
        },
        messageLabel(msg) {
            if (this.isOwnMessage(msg)) return 'You';
            if (msg.user && msg.user.name) return msg.user.name;
            return msg.sender_type === 'student' ? 'Student' : 'Staff';
        },
        avatarInitial(msg) {
            if (this.isOwnMessage(msg)) return 'Y';
            if (msg.user && msg.user.name) return msg.user.name.charAt(0).toUpperCase();
            return msg.sender_type === 'student' ? 'S' : 'A';
        },
        formatDateTime(d) { return d ? new Date(d).toLocaleString() : ''; },
        scrollToBottom() {
            const el = this.$refs.messagesContainer;
            if (el) el.scrollTop = el.scrollHeight;
        },
    },
};
</script>

<style scoped>
.vc-chat-messages {
    max-height: 360px;
    overflow-y: auto;
    padding: 1rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    border-radius: 0.875rem;
    margin-bottom: 0.875rem;
}

.vc-chat-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    text-align: center;
    color: #64748b;
    padding: 2.5rem 1rem;
    font-size: 0.875rem;
}

.vc-chat-state__icon {
    font-size: 1.5rem;
    color: #94a3b8;
}

.vc-chat-row {
    display: flex;
    align-items: flex-end;
    gap: 0.625rem;
    margin-bottom: 0.875rem;
}

.vc-chat-row--own {
    flex-direction: row-reverse;
}

.vc-chat-avatar {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    font-weight: 800;
    flex-shrink: 0;
}

.vc-chat-avatar--own {
    background: linear-gradient(135deg, #2563eb, #1e40af);
    color: #fff;
}

.vc-chat-avatar--other {
    background: #e2e8f0;
    color: #475569;
}

.vc-chat-bubble {
    max-width: 78%;
    padding: 0.75rem 1rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.vc-chat-row--own .vc-chat-bubble {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: #fff;
    border: none;
}

.vc-chat-label {
    font-size: 0.6875rem;
    font-weight: 700;
    opacity: 0.85;
    margin-bottom: 0.25rem;
}

.vc-chat-text {
    font-size: 0.875rem;
    line-height: 1.55;
    white-space: pre-wrap;
}

.vc-chat-time {
    font-size: 0.625rem;
    opacity: 0.75;
    margin-top: 0.35rem;
}

.vc-chat-composer {
    display: flex;
    gap: 0.625rem;
    align-items: flex-end;
}

.vc-chat-input {
    flex: 1;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    font-size: 0.875rem;
    resize: none;
    background: #fff;
}

.vc-chat-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.vc-chat-send {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.625rem 1rem;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    color: #fff;
    border: none;
    border-radius: 0.75rem;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
}

.vc-chat-send:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
